import { PLAN_TYPE } from "../../../../constants";

export const PERMISSIBLE_CLASS_TYPES = {
  FULL_TIME: "Full-time employees",
  PART_TIME: "Exclude part-time employees",
  SALARIED: "Employees who are paid on a salaried basis",
  NON_SALARIED: "Employees who are paid on a non-salaried basis (e.g. hourly employees)",
  RATING: "Employees whose primary site of employment is in one the following states or rating areas",
  SEASONAL: "Exclude seasonal employees",
  WAITING: "Employees who have not satisfied a waiting period for coverage",
  TEMP: "Employees hired for temporary placement at an unrelated entity",
  CBA: "Employees included in a unit covered by a collective bargaining agreement",
  NON_RESIDENT: "Nonresident aliens with no U.S.-based income",
  ALL_ELIGIBLE_EMPLOYEES: "All Eligible Employees",
  OTHER: "Other"
};

export const permissibleClassNames = Object.fromEntries(Object.entries(PERMISSIBLE_CLASS_TYPES));
export const permissibleClassTypes = Object.fromEntries(
  Object.entries(permissibleClassNames).map(([key, value]) => [value, key])
);

export const PERMISSIBLE_CLASS_TYPES_BY_PLAN = {
  [PLAN_TYPE.ICHRA]: [
    PERMISSIBLE_CLASS_TYPES.FULL_TIME,
    PERMISSIBLE_CLASS_TYPES.PART_TIME,
    PERMISSIBLE_CLASS_TYPES.SALARIED,
    PERMISSIBLE_CLASS_TYPES.NON_SALARIED,
    PERMISSIBLE_CLASS_TYPES.RATING,
    PERMISSIBLE_CLASS_TYPES.SEASONAL,
    PERMISSIBLE_CLASS_TYPES.WAITING,
    PERMISSIBLE_CLASS_TYPES.TEMP,
    PERMISSIBLE_CLASS_TYPES.CBA,
    PERMISSIBLE_CLASS_TYPES.NON_RESIDENT
  ],

  [PLAN_TYPE.QSEHRA]: [PERMISSIBLE_CLASS_TYPES.ALL_ELIGIBLE_EMPLOYEES],
  [PLAN_TYPE.GCHRA]: [PERMISSIBLE_CLASS_TYPES.ALL_ELIGIBLE_EMPLOYEES, PERMISSIBLE_CLASS_TYPES.OTHER],
  [PLAN_TYPE.EBHRA]: [PERMISSIBLE_CLASS_TYPES.ALL_ELIGIBLE_EMPLOYEES, PERMISSIBLE_CLASS_TYPES.OTHER],
  [PLAN_TYPE.THRA]: [PERMISSIBLE_CLASS_TYPES.ALL_ELIGIBLE_EMPLOYEES, PERMISSIBLE_CLASS_TYPES.OTHER]
};

// prettier-ignore
export const qualifiedBenefitNames = {
  ICHRA: {
    QUALIFIED: "Premiums only: Reimburses only insurance premiums.",
    SECTION_213D: "Premiums + out-of-pocket medical expenses: Reimburses both insurance premiums and eligible out-of-pocket medical expenses.",
    OTHER: "Other: Customize eligible expenses."
  },

  QSEHRA: {
    NON_TAXABLE_PREMIUMS_EXPENSES: "Premiums + out-of-pocket medical expenses (excluding pre-tax premiums): Reimburses both insurance premiums and eligible out-of-pocket medical expenses. Premiums paid with pre-tax dollars (e.g., through a spouse’s or parent’s employer group plan) are not eligible.",
    SECTION_213D: "Premiums + out-of-pocket medical expenses: Reimburses both insurance premiums and eligible out-of-pocket medical expenses.",
    NON_TAXABLE_PREMIUMS: "Premiums only (excluding pre-tax premiums): Reimburses only insurance premiums. Premiums paid with pre-tax dollars (e.g., through a spouse’s or parent’s employer group plan) are not eligible.",
    QUALIFIED: "Premiums only",
    OTHER: "Other: Customize eligible expenses."
  },

  GCHRA: {
    SECTION_213D_EXCEPT: "Out of Pocket Medical Expenses excluding premiums for Individual Health Coverage, Medicare, and Non-COBRA Group Coverage",
    OTHER: "Other"
  },

  EBHRA: {
    SECTION_213D_EXCEPT: "Out of Pocket Medical Expenses excluding premiums for Individual Health Coverage, Medicare, and Non-COBRA Group Coverage",
    OTHER: "Other"
  },

  THRA: {
    SECTION_213D: "Premiums + out-of-pocket medical expenses",
    OTHER: "Other"
  }
};

export const qualifiedBenefitPopovers = {
  QUALIFIED: "Health, vision, and dental premiums.",
  SECTION_213D: "All expenses defined in Section 213(d).",
  SECTION_213D_EXCEPT:
    "Reimburse out-of-pocket medical expenses excluding premiums for individual health insurance, Medicare, and non-COBRA group coverage",
  NON_TAXABLE_PREMIUMS_EXPENSES:
    "All expenses defined in Section 213(d) excluding any taxable premiums - ex. an employee is covered by their spouse's group coverage and their premium is paid pre-tax through a payroll deduction. See video for more detail.",
  NON_TAXABLE_PREMIUMS:
    "Health, vision, and dental premiums excluding any taxable premiums - ex. an employee is covered by their spouse's group coverage and their premium is paid pre-tax through a payroll deduction. See video for more. detail.",
  DEDUCTIBLES: "Group coverage insurance deductibles.",
  OTHER: "Enter expenses eligible for reimbursement."
};

export const qualifiedBenefitTypes = {};

for (const plan in qualifiedBenefitNames) {
  const benefits = qualifiedBenefitNames[plan];
  const types = Object.fromEntries(Object.entries(benefits).map(([key, value]) => [value, key]));

  qualifiedBenefitTypes[plan] = types;
}

export const PAYER_TYPE = {
  HRA_MAX: "HRA Max Benefit",
  HRA: "HRA",
  EMPLOYEE: "Employee",
  SPLIT: "Split"
};

export const payerNames = Object.fromEntries(Object.entries(PAYER_TYPE));
export const payerTypes = Object.fromEntries(Object.entries(payerNames).map(([key, value]) => [value, key]));
export const payerOptions = [PAYER_TYPE.HRA, PAYER_TYPE.EMPLOYEE, PAYER_TYPE.SPLIT];

export const MAX_BENEFIT_TYPE = {
  SELF: "SELF",
  FAMILY: "FAMILY",
  AGE: "AGE",
  FAMILY_AGE: "FAMILY_AGE",
  ACA_MINIMUM: "ACA_MINIMUM"
};

export const MAX_BENEFIT_TYPE_NAMES = {
  SELF: "Equal Amount for All Employees",
  FAMILY: "By Family Size",
  AGE: "By Age",
  FAMILY_AGE: "By Age and Family Size",
  ACA_MINIMUM: "Low Cost Affordable ICHRA"
};

export const maxBenefitNames = Object.fromEntries(Object.entries(MAX_BENEFIT_TYPE_NAMES));
export const maxBenefitTypes = Object.fromEntries(Object.entries(maxBenefitNames).map(([key, value]) => [value, key]));

export const maxBenefitOptions = {
  ICHRA: [
    MAX_BENEFIT_TYPE_NAMES.SELF,
    MAX_BENEFIT_TYPE_NAMES.FAMILY,
    MAX_BENEFIT_TYPE_NAMES.AGE,
    MAX_BENEFIT_TYPE_NAMES.FAMILY_AGE,
    MAX_BENEFIT_TYPE_NAMES.ACA_MINIMUM
  ],

  QSEHRA: [
    MAX_BENEFIT_TYPE_NAMES.SELF,
    MAX_BENEFIT_TYPE_NAMES.FAMILY,
    MAX_BENEFIT_TYPE_NAMES.AGE,
    MAX_BENEFIT_TYPE_NAMES.FAMILY_AGE
  ]
};

export const MAX_BENEFIT_COVERAGE = {
  SELF: "SELF",
  SPOUSE: "SPOUSE",
  CHILDREN: "CHILDREN",
  SPOUSE_CHILDREN: "SPOUSE_CHILDREN"
};

export const MAX_BENEFIT_COVERAGE_NAMES = {
  SELF: "Self",
  SPOUSE: "Self & Spouse",
  CHILDREN: "Self & Children",
  SPOUSE_CHILDREN: "Self, Spouse, & Children"
};

export const MAX_BENEFIT_AGE_LIST = {
  MINIMUM_AGE: 21,
  MAXIMUM_AGE: 64
};

export const maxBenefitLabels = [
  MAX_BENEFIT_COVERAGE_NAMES.SELF,
  MAX_BENEFIT_COVERAGE_NAMES.SPOUSE,
  MAX_BENEFIT_COVERAGE_NAMES.CHILDREN,
  MAX_BENEFIT_COVERAGE_NAMES.SPOUSE_CHILDREN
];

export const MAX_BENEFIT_BUTTONS = [
  {
    text: MAX_BENEFIT_COVERAGE_NAMES.SELF,
    icon: "person",
    check: ({ items }) => items.self && !items.children && !items.spouse
  },
  {
    text: MAX_BENEFIT_COVERAGE_NAMES.SPOUSE,
    icon: "person",
    check: ({ items }) => items.self && !items.children && items.spouse
  },
  {
    text: MAX_BENEFIT_COVERAGE_NAMES.CHILDREN,
    icon: "person",
    check: ({ items }) => items.self && items.children && !items.spouse
  },
  {
    text: MAX_BENEFIT_COVERAGE_NAMES.SPOUSE_CHILDREN,
    icon: "person",
    check: ({ items }) => items.self && items.children && items.spouse
  }
];

export const REIMBURSEMENT_DISTRIBUTION_TIMING = {
  DAILY: "DAILY",
  MONTHLY: "MONTHLY",
  PAYROLL: "PAYROLL"
};

export const REIMBURSEMENT_DISTRIBUTION_METHOD = {
  SALUSION: "SALUSION",
  COMPANY: "COMPANY",
  OTHER: "OTHER"
};
